import { useState } from 'react';
import { Copy, Check, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PixPaymentProps {
  pixCode: string | null;
  amount: number;
}

export function PixPayment({ pixCode }: PixPaymentProps) {
  const [copied, setCopied] = useState(false);

  // Generate placeholder PIX code if not provided
  const displayCode = pixCode || `00020126580014br.gov.bcb.pix0136${Math.random().toString(36).slice(2)}5204000053039865802BR5913EMPRESA_DEMO6008SAOPAULO62070503***6304`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(displayCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Generate a realistic QR code pattern based on the code
  const generateQrPattern = () => {
    const size = 29; // Standard QR code size
    const cells = [];
    const seed = displayCode.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    
    // Helper to check if position is in finder pattern area
    const isFinderPattern = (x: number, y: number) => {
      const inTopLeft = x < 7 && y < 7;
      const inTopRight = x >= size - 7 && y < 7;
      const inBottomLeft = x < 7 && y >= size - 7;
      return inTopLeft || inTopRight || inBottomLeft;
    };

    // Helper to draw finder pattern
    const drawFinderCell = (x: number, y: number, offsetX: number, offsetY: number) => {
      const localX = x - offsetX;
      const localY = y - offsetY;
      
      // Outer border (7x7)
      if (localX === 0 || localX === 6 || localY === 0 || localY === 6) return true;
      // White ring (5x5 inside)
      if (localX === 1 || localX === 5 || localY === 1 || localY === 5) return false;
      // Inner square (3x3)
      if (localX >= 2 && localX <= 4 && localY >= 2 && localY <= 4) return true;
      return false;
    };

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        let isFilled = false;

        // Top-left finder pattern
        if (x < 7 && y < 7) {
          isFilled = drawFinderCell(x, y, 0, 0);
        }
        // Top-right finder pattern
        else if (x >= size - 7 && y < 7) {
          isFilled = drawFinderCell(x, y, size - 7, 0);
        }
        // Bottom-left finder pattern
        else if (x < 7 && y >= size - 7) {
          isFilled = drawFinderCell(x, y, 0, size - 7);
        }
        // Timing patterns (alternating lines)
        else if (x === 6) {
          isFilled = y % 2 === 0;
        }
        else if (y === 6) {
          isFilled = x % 2 === 0;
        }
        // Alignment pattern (small square in the middle-right area)
        else if (x >= 20 && x <= 24 && y >= 20 && y <= 24) {
          const ax = x - 20;
          const ay = y - 20;
          if (ax === 0 || ax === 4 || ay === 0 || ay === 4) isFilled = true;
          else if (ax === 2 && ay === 2) isFilled = true;
        }
        // Data pattern (pseudo-random based on seed)
        else if (!isFinderPattern(x, y)) {
          const hash = (seed * (x + 1) * (y + 1) + x * 31 + y * 17) % 100;
          isFilled = hash < 45;
        }

        if (isFilled) {
          cells.push(
            <rect
              key={`${x}-${y}`}
              x={x * 8}
              y={y * 8}
              width="8"
              height="8"
              fill="currentColor"
            />
          );
        }
      }
    }
    return cells;
  };

  return (
    <div className="space-y-6">
      {/* QR Code */}
      <div className="flex flex-col items-center">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <svg 
            viewBox="0 0 232 232" 
            className="w-56 h-56 text-[hsl(var(--basic-900))]"
          >
            {generateQrPattern()}
          </svg>
        </div>
        
        {/* Scan instruction */}
        <div className="mt-4 flex items-center gap-2 text-[hsl(var(--accent-600))] bg-[hsl(var(--accent-50))] px-4 py-2 rounded-full">
          <Smartphone className="h-4 w-4" />
          <span className="text-sm font-medium">Escaneie com o app do banco</span>
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">ou copie o código</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* PIX Code */}
      <div>
        <div className="flex gap-2">
          <div className="flex-1 p-4 bg-[hsl(var(--basic-50))] rounded-xl border-2 border-dashed border-[hsl(var(--basic-200))] font-mono text-sm break-all text-[hsl(var(--basic-600))]">
            {displayCode.slice(0, 60)}...
          </div>
          <Button 
            onClick={handleCopy}
            className={`h-auto px-6 transition-all ${
              copied 
                ? 'bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]' 
                : 'bg-[hsl(var(--basic-900))] hover:bg-[hsl(var(--basic-800))]'
            }`}
          >
            {copied ? (
              <span className="flex items-center gap-2">
                <Check className="h-4 w-4" />
                Copiado!
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Copy className="h-4 w-4" />
                Copiar
              </span>
            )}
          </Button>
        </div>
        {copied && (
          <p className="text-sm text-[hsl(var(--accent-600))] mt-2 text-center font-medium">
            ✓ Código PIX copiado! Cole no app do seu banco.
          </p>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-gradient-to-br from-[hsl(var(--basic-50))] to-[hsl(var(--basic-100))] rounded-xl p-5 border">
        <h4 className="font-bold text-sm text-[hsl(var(--basic-800))] mb-3 flex items-center gap-2">
          <span className="h-6 w-6 rounded-full bg-[hsl(var(--accent-500))] text-white flex items-center justify-center text-xs">?</span>
          Como pagar com PIX:
        </h4>
        <ol className="text-sm text-[hsl(var(--basic-600))] space-y-2">
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">1</span>
            <span>Abra o aplicativo do seu banco</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">2</span>
            <span>Escolha pagar com PIX</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">3</span>
            <span>Escaneie o QR Code ou cole o código copiado</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-5 w-5 rounded-full bg-[hsl(var(--basic-200))] text-[hsl(var(--basic-600))] flex items-center justify-center text-xs font-bold shrink-0">4</span>
            <span>Confirme e pronto!</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
